import { GoogleMapsIcon } from "../icons";
import { SvgZillow, SvgSendCard, SvgAreaPro } from "./svg";
import { Listing } from "src/types/listing";
import { PrinterIcon, History, MoreVertical } from "lucide-react";
import { useSendCards, useSendCardsAccess } from "src/components/sendCards";
import { useAreaPro } from "src/components/areaPro";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "src/components/shadcn/DropDownMenu";
import Tooltip from "src/components/core/Tooltip";
import { PartnerSyncButtons } from "./PartnerSyncButtons";

export interface PropertyActionsToolbarProps {
  listing: Listing;
  mapLink?: string | null;
  title: string;
  onPrintClick?: () => void;
  sendCardsTemplateCategory?: string;
  sendCardCount?: number;
}

export const PropertyActionsToolbar = ({
  listing,
  mapLink,
  title,
  onPrintClick,
  sendCardsTemplateCategory = "",
  sendCardCount = 0,
}: PropertyActionsToolbarProps) => {
  const { sendCards, viewSentCards } = useSendCards();
  const hasSendCardsAccess = useSendCardsAccess();
  const { openAreaPro } = useAreaPro();

  return (
    <div className="property-actions-toolbar flex flex-row items-center gap-2 text-gray-primary [&>*]:shrink-0 [&_svg]:shrink-0">
      {mapLink && (
        <Tooltip content="View property on Google Maps">
          <a href={mapLink} target="_blank" rel="noopener noreferrer">
            <GoogleMapsIcon
              title=""
              titleId=""
              className="cursor-pointer hover:text-primary transition-colors"
            />
          </a>
        </Tooltip>
      )}
      <Tooltip content="View property on Zillow">
        <a
          href={`https://www.zillow.com/homes/${title.replace(/ /g, "-")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center"
        >
          <SvgZillow
            title=""
            titleId=""
            width={24}
            height={24}
            className="hover:text-primary transition-colors cursor-pointer"
          />
        </a>
      </Tooltip>
      {onPrintClick && (
        <Tooltip content="Print property">
          <PrinterIcon
            className="w-6 h-6 cursor-pointer hover:text-primary transition-colors"
            onClick={onPrintClick}
          />
        </Tooltip>
      )}
      <Tooltip content="View property Insights on AreaPro">
        <div className="flex items-center">
          <SvgAreaPro
            title=""
            titleId=""
            width={28}
            height={28}
            className="cursor-pointer hover:text-primary transition-colors"
            onClick={() => openAreaPro(listing)}
          />
        </div>
      </Tooltip>
      {hasSendCardsAccess && (
        <>
          <Tooltip content="Send a handwritten card to property">
            <div className="relative">
              <SvgSendCard
                title=""
                titleId=""
                width={24}
                height={24}
                className="cursor-pointer hover:text-primary transition-colors"
                onClick={() => sendCards([listing], sendCardsTemplateCategory)}
              />
              {sendCardCount > 0 && (
                <div className="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-semibold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                  {sendCardCount}
                </div>
              )}
            </div>
          </Tooltip>
          <PartnerSyncButtons listing={listing} />
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <MoreVertical className="w-6 h-6 cursor-pointer hover:text-primary transition-colors" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => viewSentCards(listing)}
              >
                <History className="mr-2 h-4 w-4" />
                View Sent Cards
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  );
};
